import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, ArrowBack } from '@material-ui/icons';
import { BarthelRecord } from '../../../constants/Records';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    actionCell: {
      width: 120,
    },
    emptyMessage: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
  }),
);

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: BarthelRecord[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (uniqueId: string) => void;
  onDelete: (uniqueId: string, date: string) => void;
  onResetFlags: () => void;
}

const BarthelRecordList: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  executing,
  loaded,
  deleted,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BarthelRecord | null>(null);

  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records`);
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/barthel/create`);
  };

  const handleEdit = (record: BarthelRecord) => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/barthel/${record.record_time}/edit`);
  };

  const handleDeleteClick = (record: BarthelRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord && uniqueId) {
      onDelete(uniqueId, selectedRecord.record_time);
    }
  };

  // 合計スコアをクライアントで計算
  const calculateTotalScore = (record: BarthelRecord): number => {
    const fields: (keyof BarthelRecord)[] = [
      'feeding',
      'transfers',
      'grooming',
      'toilet_use',
      'bathing',
      'mobility',
      'stairs',
      'dressing',
      'bowels',
      'bladder',
    ];
    return fields.reduce((sum, field) => {
      const value = record[field];
      if (typeof value === 'number') {
        return sum + value;
      }
      if (typeof value === 'string') {
        const num = parseInt(value, 10);
        return sum + (isNaN(num) ? 0 : num);
      }
      return sum;
    }, 0);
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; バーセルインデックス
      </Typography>

      <div className={classes.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">バーセルインデックス一覧</Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </div>

      {executing && !loaded ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : records.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>バーセルインデックス記録はありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>記録日</TableCell>
                <TableCell>合計スコア</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.record_time}>
                  <TableCell>{record.record_time}</TableCell>
                  <TableCell>{calculateTotalScore(record)}点</TableCell>
                  <TableCell className={classes.actionCell}>
                    <IconButton size="small" onClick={() => handleEdit(record)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(record)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="バーセルインデックスの削除"
        content={`${selectedRecord?.record_time} の記録を削除しますか？`}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default BarthelRecordList;
